import {
  createItem,
  deleteItem,
  getItem,
  listItems,
  updateItem,
} from './sharepoint';

const LIST = 'requests';

/**
 * Team-member "Requests" — a single SharePoint list covering all six
 * request types (Vest, Shirt, Business Cards, Badge Card, Address
 * Change, Phone Change). The list's `RequestType` column is the
 * discriminator; each type uses a subset of the per-type columns
 * (Quantity/Size on apparel, CardNameLine etc. on business cards,
 * NewStreet etc. on address changes, NewPhone on phone changes).
 * Unused columns stay null.
 *
 * Lifecycle status values (in order):
 *   Submitted → In Progress → Ordered → Fulfilled
 *                                     ↳ Declined
 *
 * Any status write also stamps ReviewedBy / ReviewedAt. That lets the
 * requester see who last touched their request and when, and keeps the
 * audit trail in one place without a separate history table.
 */

export const REQUEST_TYPES = [
  'Vest',
  'Shirt',
  'Business Cards',
  'Badge Card',
  'Address Change',
  'Phone Change',
  'Emergency Contact Change',
  'Shirt Size Change',
  // Auto-apply vest size + color update from the Safety Vest tile on
  // My Account. Same pattern as Shirt Size Change — writes back to the
  // team record immediately and files a Fulfilled request row for audit.
  'Vest Size Change',
];

export const REQUEST_STATUSES = [
  'Submitted',
  'In Progress',
  'Ordered',
  'Fulfilled',
  'Declined',
];

// Final/terminal statuses — rendered dimmer in the Director inbox and
// filtered out of the default "open" view.
export const TERMINAL_STATUSES = new Set(['Fulfilled', 'Declined']);

/**
 * Apparel sizes used by Vest + Shirt requests. Free text on the list
 * so we can extend later without a schema change, but we offer a
 * reasonable default dropdown in the form.
 */
export const APPAREL_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
export const SHIRT_FITS = ["Men's", "Women's"];
export const SHIRT_STYLES = ['Polo', 'T-Shirt', 'Button-Down'];
export const CARD_QUANTITIES = [250, 500, 1000];

/**
 * Vest colors offered on the request form + Team Member profile. Proxemics
 * only stocks Yellow and Orange today — that's the canonical list. The
 * various forms (TeamForm, MyAccount Update Vest, Vest request) still
 * carry legacy "Other" handling with a free-text fallback input; that
 * code stays inert now (nothing can pick "Other") but keeps existing
 * records that stored a custom color rendering correctly.
 */
export const VEST_COLORS = ['Yellow', 'Orange'];

export async function listRequests(opts) {
  return listItems(LIST, {
    // Newest first. SharePoint's Created column is always present; our
    // RequestedAt column is what we actually set but Created is a
    // reliable sort even for rows missing RequestedAt.
    orderby: 'fields/Created desc',
    ...opts,
  });
}

export async function getRequest(id) {
  return getItem(LIST, id);
}

/**
 * Create a new request. Callers pass a shallow object with `RequestType`
 * plus whatever type-specific fields apply; we stamp RequestedBy /
 * RequestedByEmail / RequestedAt and set initial Status = Submitted.
 *
 * `Title` is set to a human-readable summary ("Vest — M × 2") so the
 * native SharePoint list view is scannable without the portal UI.
 */
export async function createRequest(model) {
  const now = new Date().toISOString();
  const payload = {
    Status: 'Submitted',
    RequestedAt: model.RequestedAt || now,
    ...model,
  };
  payload.Title = payload.Title || summariseRequest(payload);
  return createItem(LIST, payload);
}

/**
 * Update an arbitrary request row. Used by the Director inbox to change
 * status, leave a StatusNote, and stamp ReviewedBy / ReviewedAt.
 *
 * Passing only `{ Status, StatusNote }` is the common path; the helper
 * below (`updateRequestStatus`) wraps that so callers don't have to
 * remember the audit fields.
 */
export async function updateRequest(id, fields) {
  return updateItem(LIST, id, fields);
}

/**
 * Change a request's status and leave an optional note. Automatically
 * stamps the reviewer + timestamp so the audit trail stays honest.
 *
 * @param {string} id             — list item id
 * @param {object} args
 * @param {string} args.status    — one of REQUEST_STATUSES
 * @param {string} [args.note]    — optional StatusNote
 * @param {string} [args.reviewer]— display name of the Director acting
 */
export async function updateRequestStatus(id, { status, note, reviewer }) {
  if (!REQUEST_STATUSES.includes(status)) {
    throw new Error(`updateRequestStatus: unknown status "${status}"`);
  }
  return updateItem(LIST, id, {
    Status: status,
    StatusNote: note || '',
    ReviewedBy: reviewer || '',
    ReviewedAt: new Date().toISOString(),
  });
}

export async function deleteRequest(id) {
  return deleteItem(LIST, id);
}

/**
 * Build a short single-line label for a request ("Vest — M × 2",
 * "Business Cards — Chris Maxwell × 500"). Used both as the row's
 * SharePoint Title on create and as a fallback display label when the
 * Director inbox needs one line.
 */
export function summariseRequest(r) {
  if (!r) return '';
  switch (r.RequestType) {
    case 'Vest':
    case 'Shirt': {
      const qty = r.Quantity ? ` × ${r.Quantity}` : '';
      const size = r.Size ? ` — ${r.Size}` : '';
      // Vest-only: append the color to the title so the Director inbox
      // shows "Vest — M, Yellow × 2" at a glance.
      const color =
        r.RequestType === 'Vest' && r.VestColor ? `, ${r.VestColor}` : '';
      return `${r.RequestType}${size}${color}${qty}`;
    }
    case 'Business Cards': {
      const qty = r.CardQuantity ? ` × ${r.CardQuantity}` : '';
      const name = r.CardNameLine ? ` — ${r.CardNameLine}` : '';
      return `Business Cards${name}${qty}`;
    }
    case 'Badge Card': {
      const who = [r.BadgeFirstName, r.BadgeLastName]
        .filter(Boolean)
        .join(' ');
      const plant = r.BadgePlantCode ? ` (${r.BadgePlantCode})` : '';
      return `Badge Card${who ? ` — ${who}` : ''}${plant}`;
    }
    case 'Address Change': {
      const city = [r.NewCity, r.NewState].filter(Boolean).join(', ');
      return `Address Change${city ? ` — ${city}` : ''}`;
    }
    case 'Phone Change': {
      return `Phone Change${r.NewPhone ? ` — ${r.NewPhone}` : ''}`;
    }
    case 'Emergency Contact Change': {
      const who = r.NewEmergencyContactName
        ? ` — ${r.NewEmergencyContactName}`
        : '';
      return `Emergency Contact Change${who}`;
    }
    case 'Shirt Size Change': {
      const parts = [r.NewShirtSize, r.NewShirtFit].filter(Boolean).join(', ');
      return `Shirt Size Change${parts ? ` — ${parts}` : ''}`;
    }
    case 'Vest Size Change': {
      // When the color is "Other", prefer the fill-in so the summary
      // reads "Yellow, M" / "Hi-Vis Green, L" — never the bare "Other".
      const color =
        r.NewVestColor === 'Other'
          ? r.NewVestColorOther || 'Other'
          : r.NewVestColor;
      const parts = [r.NewVestSize, color].filter(Boolean).join(', ');
      return `Vest Size Change${parts ? ` — ${parts}` : ''}`;
    }
    default:
      return r.RequestType || 'Request';
  }
}
